import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  UserCheck,
  UserX,
  RefreshCw
} from 'lucide-react';
import { gestaoService } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const GestaoCobranca = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('hoje');

  const { data: gestaoData, isLoading, error, refetch } = useQuery({
    queryKey: ['gestao-cobranca', selectedPeriod],
    queryFn: () => gestaoService.dashboard(),
    staleTime: 300000, // 5 minutos
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestão de Cobrança</h1>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestão de Cobrança</h1>
          <button
            onClick={() => refetch()}
            className="btn-primary flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
        <div className="card">
          <div className="text-center text-red-600 dark:text-red-400">
            Erro ao carregar dados de gestão
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestão de Cobrança</h1>
          <p className="text-gray-600 dark:text-gray-400">Controle e acompanhamento de cobranças por funcionário</p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn-secondary flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Funcionários</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.total_funcionarios || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cobranças Hoje</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.total_cobrancas_hoje || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Calendar className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cobranças Semana</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.total_cobrancas_semana || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cobranças Mês</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.total_cobrancas_mes || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Status das Cobranças */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pendentes</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.cobrancas_pendentes || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Não Finalizadas</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.cobrancas_nao_finalizadas || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Finalizadas</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {gestaoData?.cobrancas_finalizadas || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Funcionários */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
          Top Funcionários (Efetividade)
        </h2>
        
        {gestaoData?.top_funcionarios && gestaoData.top_funcionarios.length > 0 ? (
          <div className="space-y-3">
            {gestaoData.top_funcionarios.map((funcionario, index) => (
              <div key={funcionario.funcionario} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {funcionario.funcionario}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {funcionario.total_cobrancas} cobranças • {funcionario.cobrancas_efetivas} efetivas
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">
                    {funcionario.percentual_efetividade}%
                  </p>
                  <p className="text-xs text-gray-500">
                    {funcionario.cobrancas_ultimos_30_dias} últimos 30 dias
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Nenhum funcionário encontrado
          </div>
        )}
      </div>

      {/* Funcionários sem Cobrança */}
      {gestaoData?.funcionarios_sem_cobranca && gestaoData.funcionarios_sem_cobranca.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <UserX className="h-5 w-5 mr-2 text-red-600" />
            Funcionários sem Cobrança (Últimos 30 dias)
          </h2>
          
          <div className="space-y-2">
            {gestaoData.funcionarios_sem_cobranca.map((funcionario) => (
              <div key={funcionario.funcionario} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center">
                  <UserX className="h-5 w-5 text-red-600 mr-2" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {funcionario.funcionario}
                  </span>
                </div>
                <span className="text-sm text-red-600 dark:text-red-400">
                  Sem atividade
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabela Completa de Funcionários */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Relatório Completo por Funcionário
        </h2>
        
        {gestaoData?.cobrancas_por_funcionario && gestaoData.cobrancas_por_funcionario.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Funcionário</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Total Cobranças</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Efetivas</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Efetividade</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Últimos 30 dias</th>
                </tr>
              </thead>
              <tbody>
                {gestaoData.cobrancas_por_funcionario.map((funcionario) => (
                  <tr key={funcionario.funcionario} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">
                      {funcionario.funcionario}
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      {funcionario.total_cobrancas}
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      {funcionario.cobrancas_efetivas}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`badge ${
                        funcionario.percentual_efetividade >= 80 ? 'badge-success' :
                        funcionario.percentual_efetividade >= 60 ? 'badge-warning' :
                        'badge-danger'
                      }`}>
                        {funcionario.percentual_efetividade}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      {funcionario.cobrancas_ultimos_30_dias}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Nenhum funcionário encontrado
          </div>
        )}
      </div>
    </div>
  );
};

export default GestaoCobranca; 